import { Directive, OnChanges, ElementRef, Input, Renderer2, OnInit, DestroyRef, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { first } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Renders the CFA Design System LoadingIndicator (circle-dots) over the host.
 * Markup/CSS mirrors @cfa/react-core LoadingIndicator (size=md).
 */
@Directive({
  // tslint:disable-next-line:directive-selector
  selector: '[cfaLoading]',
  standalone: false
})
export class CfaLoadingDirective implements OnInit, OnChanges {

    @Input() cfaLoading = false;

    private loadingContainerBG: HTMLElement;
    private loadingIndicator: HTMLElement;

    private initialized = false;
    private initializedObs?: Observable<boolean>;

    private currPointerStyle = '';
    private currUserSelectStyle = '';

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    constructor(public el: ElementRef, public renderer: Renderer2) {
      this.loadingContainerBG = this.renderer.createElement('div');
      this.loadingIndicator = this.buildLoadingIndicator();
    }

    private buildLoadingIndicator(): HTMLElement {
      const root = this.renderer.createElement('div');
      this.renderer.addClass(root, 'cfa-loading-indicator');
      this.renderer.setAttribute(root, 'role', 'status');
      this.renderer.setAttribute(root, 'aria-label', 'loading indicator');

      const container = this.renderer.createElement('div');
      this.renderer.addClass(container, 'cfa-loading-indicator__container');

      const spinner = this.renderer.createElement('div');
      this.renderer.addClass(spinner, 'cfa-loading-indicator__spinner');
      this.renderer.setAttribute(spinner, 'aria-hidden', 'true');

      this.renderer.appendChild(container, spinner);
      this.renderer.appendChild(root, container);
      return root;
    }

    private overlayBackground(): string {
      const isDarkMode = document.body.classList.contains('spotlight-dark-mode');
      return isDarkMode ? 'rgba(27,37,45,0.6)' : 'rgba(255,255,255,0.6)';
    }

    private init(): Observable<boolean> {
      if (this.initialized) {
        return of(this.initialized);
      } else {
        if (!this.initializedObs) {
          this.initializedObs = new Observable((observer: any) => {
            this.renderer.appendChild(this.el.nativeElement, this.loadingContainerBG);
            this.renderer.appendChild(this.loadingContainerBG, this.loadingIndicator);

            this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');

            this.renderer.setStyle(this.loadingContainerBG, 'display', 'none');
            this.renderer.setStyle(this.loadingContainerBG, 'align-items', 'center');
            this.renderer.setStyle(this.loadingContainerBG, 'justify-content', 'center');
            this.renderer.setStyle(this.loadingContainerBG, 'background-color', this.overlayBackground());
            this.renderer.setStyle(this.loadingContainerBG, 'width', '100%');
            this.renderer.setStyle(this.loadingContainerBG, 'height', '100%');
            this.renderer.setStyle(this.loadingContainerBG, 'position', 'absolute');
            this.renderer.setStyle(this.loadingContainerBG, 'inset', '0');
            this.renderer.setStyle(this.loadingContainerBG, 'z-index', '5');

            this.initialized = true;
            observer.next(this.initialized);
          });
        }
        return this.initializedObs;
      }
    }

    ngOnInit() {
      this.init()
        .pipe(
          first(),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe();
    }

    ngOnChanges() {
      this.init()
        .pipe(
          first(),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => {
          this.processChange();
        });
    }

    private processChange() {
      if (this.cfaLoading) {
        this.currPointerStyle = (this.el.nativeElement as HTMLElement).style.pointerEvents;
        this.currUserSelectStyle = (this.el.nativeElement as HTMLElement).style.userSelect;
        this.renderer.setStyle(this.el.nativeElement, 'pointer-events', 'none');
        this.renderer.setStyle(this.el.nativeElement, 'user-select', 'none');

        this.renderer.setStyle(this.loadingContainerBG, 'background-color', this.overlayBackground());
        this.renderer.setStyle(this.loadingContainerBG, 'display', 'flex');
      } else if (this.initialized) {
        this.renderer.setStyle(this.loadingContainerBG, 'display', 'none');

        if (this.currPointerStyle) {
          this.renderer.setStyle(this.el.nativeElement, 'pointer-events', this.currPointerStyle);
        } else {
          this.renderer.removeStyle(this.el.nativeElement, 'pointer-events');
        }

        if (this.currUserSelectStyle) {
          this.renderer.setStyle(this.el.nativeElement, 'user-select', this.currUserSelectStyle);
        } else {
          this.renderer.removeStyle(this.el.nativeElement, 'user-select');
        }
      }
    }
}
